"use strict";
import axios from '../axios-on-rails';

/**
 * Lazy loading for recommend counts in the job search screen.
 * 
 * Counting how many recommendations are there for a job can be slow, so this provides a lazy
 * load method.
 * 
 *   Algorithm:
 * - Pick the job that's in the middle of the screen.
 * - Starting from this job, select some jobs that are above and below in the list (max limit is set).
 * - Load counts for all those IDs.
 * - Only one subset is fetched at the same time, and once it completes, the next one is started.
 * - If the user changed scroll position, then when the next subset starts, it will use the
 *   position at the moment the loading started.
 * 
 *   Note:
 *   This can be executed safely many times (by executing fetchGradually(true) several times), this will not
 *   repeat IDs, and can make it faster because of increased concurrency (in the backend). However
 *   doing it too many times might increase 'user perceived time' (time it takes to load the jobs
 *   the user is looking at) v/s 'total load time' (of all jobs), so it's necessary to experiment and
 *   find the best number. Per batch number also influences these times.
 * 
 */

/**
 * Representation for the whole job list, and the screen.
 * The purpose is to get which jobs are visible to the user, and manage which ones are loaded,
 * and determine which ones must be loaded next.
 * Note that in the comments, 'Job ID' refers to a JobTranslation instance, not a single job.
 */
class JobListModel {
  constructor(){
    this.loadedIds = {};
    this.loadingIds = {};
    this.allIds = $('[data-job-translation-id][data-recommend=true]').map((_, tr) => $(tr).attr('data-job-translation-id'))
                                                                     .get();
  }

  /**
   * Returns the index (position) of the job that's closer to the middle of the screen.
   * It returns 0 if for some reason it couldn't be calculated.
   * 
   * @return {number} Position
   */
  positionCloserToMiddle(){
    if(this.allIds.length == 0) return null;
    var position = null;
    var minDifference = 1000000;
    $('[data-job-translation-id][data-recommend=true]').each((idx, tr) => {
      var eTop = $(tr).offset().top;
      var screenHeight = $(window).height();
      var posY = (eTop - $(window).scrollTop())/screenHeight;
      var diff = Math.abs(posY - 0.5);
      if(diff < minDifference){
        position = idx;
        minDifference = diff;
      }
    });
    return isFinite(position) ? position : 0; // In case the code above is not supported by some obscure browser.
  }

  /**
   * Whether the load (of recommendation counts) is complete or not.
   * 
   * @return {boolean}
   */
  loadComplete(){
    return this.allIds.every(id => this.loadedIds[id]);
  }

  /**
   * Gets a batch of Job IDs to fetch counts. If no job is visible, it will get the nearest ones
   * that haven't been fetched yet.
   * 
   * @param {number} amount How many IDs to return.
   * @return {Array} Subset of IDs near the center of the screen.
   */
  getIdsNearMiddle(amount){
    if(this.allIds.length === 0) return [];
    var positionsFromCenter = [];

    var i = this.positionCloserToMiddle();
    var j = i + 1;
    while(positionsFromCenter.length < this.allIds.length){
      if(i >= 0) positionsFromCenter.push(i--);
      if(j < this.allIds.length) positionsFromCenter.push(j++);
    }

    return positionsFromCenter.map(p => this.allIds[p])
                              .filter(id => this.shouldLoadId(id))
                              .slice(0, amount);
  }

  /**
   * Sets a Job ID as loaded.
   *
   * @param {number} id Job ID which count was loaded.
   * @return {void}
   */
  setLoaded(id){
    this.loadingIds[id] = false;
    this.loadedIds[id] = true;
  }

  /**
   * Sets a Job ID as loading.
   *
   * @param {number} id Job ID that was dispatched to have its count fetched.
   * @return {void}
   */
  dispatchId(id){
    this.loadingIds[id] = true;
  }

  /**
   * Checks if a Job ID isn't either loaded or loading. In other words, needs to be loaded.
   *
   * @param {number} id Job ID to check.
   * @return {boolean}
   */
  shouldLoadId(id){
    return !this.loadedIds[id] && !this.loadingIds[id];
  }
}

/**
 * Loads counts for jobs in the job search view.
 * Note that in the comments, 'Job ID' refers to a JobTranslation instance, not a single job.
 */
export default class JobSearch {
  initialize(perBatch){
    this.perBatch = perBatch;
    this.jobList = new JobListModel();
  }

  /**
   * Fetches one batch of Job IDs (subset of all Job IDs).
   *
   * @param {boolean} continueWithNextBatch Whether it must execute again after it finishes (with the next batch).
   * @return {void}
   */
  fetchGradually(continueWithNextBatch = false){
    const fetchCompleteCallback = () => {
      var currTime = new Date().getTime();
      console.log('Batch execution time: ', currTime - batchStartTime);

      if(this.jobList.loadComplete()) return console.log('(Complete) Time: ', currTime - this.startTime);
      if(continueWithNextBatch) this.fetchGradually(true);
    };

    var batchStartTime = new Date().getTime();
    if(this.startTime == null) this.startTime = batchStartTime;
    this.fetchCounts(this.jobList.getIdsNearMiddle(this.perBatch), fetchCompleteCallback);
  }

  /**
   * Fetches recommendation counts for an array of Job IDs.
   *
   * @param {Array} ids Job IDs to fetch recommendation counts. IDs are JobTranslation IDs.
   * @param {function} callback Function to execute after the fetching finishes.
   * @return {void}
   */
  fetchCounts(ids, callback = () => {}){
    if(ids.length === 0) return;
    ids.forEach(id => this.jobList.dispatchId(id));
    axios({
      method: 'GET',
      url: Routes.member_recommendations_count_members_recommended_for_job_path({ ids })
    }).then(res => {
      if(!res.data && !res.data.counts) return;
      this.updateCountIcon(res.data.counts);
      callback();
    });
  }

  /**
   * Updates the counts on screen.
   *
   * @param {object} counts Map with Job ID => count.
   * @return {void}
   */
  updateCountIcon(counts){
    for(var id in counts){
      var count = counts[id];
      if(count > 99) count = '99+';
      var $tr = $(`[data-job-translation-id=${id}]`);
      $tr.find('.recommend-bubble').removeClass('-loading');
      $tr.find('.recommend-bubble-number').show().html(count);
      this.jobList.setLoaded(id);
    }
  }

  hideScoutSubmission(){
    $('.scout-submission-toast').hide();
  }
}
